// src/router/index.js
import React, { useContext, lazy, Suspense } from "react";
import { Routes, Route, Outlet } from "react-router-dom";

import { ScrollToTop } from "../components";
import { AppContext } from "../context/AppContext";

import RequireAuth from "./RequireAuth";
import RequireRole from "./RequireRole";


import CarePlanView from "../pages/CarePlanView";
import CarePlanPrintView from "../components/CarePlans/CarePlanPrintView";
import StaffAbsencesCalendarPage from "../pages/StaffAbsencesCalendarPage";

import DashboardModern from "../pages/DashboardModern";
import StaffAbsencesMonth from "../pages/StaffAbsencesMonth";

// lazy import recommended
const IncidentsList = React.lazy(() => import("../pages/Incidents/IncidentsList"));
const IncidentDetail = React.lazy(() => import("../pages/Incidents/IncidentDetail"));

// ---------- helpers ----------
const lazyNamed = (importer, exportName) =>
  lazy(() =>
    importer().then((mod) => ({
      default: mod[exportName],
    }))
  );

const RouteLoading = () => (
  <div style={{ padding: 16 }}>Loading…</div>
);

// ---------- lazy pages/components ----------

// Public auth pages
const AuthRegister = lazy(() => import("../pages/auths/AuthRegister"));
const AuthLogin = lazy(() => import("../pages/auths/AuthLogin"));
const FamilyAuthLogin = lazy(() => import("../pages/auths/FamilyAuthLogin"));
const AuthReset = lazy(() => import("../pages/auths/AuthReset"));
const AuthLogoff = lazy(() => import("../pages/auths/AuthLogoff"));

// Core
const Blank = lazy(() => import("../pages/Blank"));
const Home = lazy(() => import("../pages/Home"));


// Your app pages/components
const Jobsheets = lazy(() => import("../pages/Jobsheets"));
const Clients = lazy(() => import("../pages/Clients"));
const Jobsheet = lazy(() => import("../pages/Jobsheet/Jobsheet"));
const Client = lazy(() => import("../components/Client/Client"));
const JobSchedule = lazy(() => import("../pages/JobSchedule"));
const JobScheduleStaff = lazy(() => import("../pages/JobScheduleStaff"));
const JobScheduleStaffMyAccount = lazy(() => import("../pages/JobScheduleStaffMyAccount"));
const MyAccountPage = lazy(() => import("../pages/MyAccountPage"));
const ClientDailyScheduleBoard = lazy(() => import("../pages/ClientDailyScheduleBoard"));
// const JobScheduleTimeLine = lazy(() => import("../pages/JobScheduleTimeLine")); // (kept commented as your original)

const Staff = lazy(() => import("../components/Staff/Staff"));
const StaffList = lazy(() => import("../components/Staff/StaffList"));
const StaffListAdmin = lazy(() => import("../components/Staff/StaffListAdmin"));

const RegionSettings = lazy(() => import("../components/Settings/RegionSettings"));
const ProductSettings = lazy(() => import("../components/Settings/ProductSettings"));
const JobRoleSettings = lazy(() => import("../components/Settings/JobRoleSettings"));
const SkillsSettings = lazy(() => import("../components/Settings/SkillsSettings"));

const Subscriptions = lazy(() => import("../components/Management/Subscriptions"));
const Tenants = lazy(() => import("../components/Management/Tenants"));
const ApiUsage = lazy(() => import("../components/Management/ApiUsage"));

const Subscription = lazy(() => import("../components/Client/Subscription"));
const TodaysVisitsV2 = lazy(() => import("../components/Jobsheet/TodaysVisitsV2"));
const LiveVisitsBoardRedesigned = lazy(() => import("../pages/LiveVisitsBoardRedesigned"));
const ProcessRecurringTask = lazy(() => import("../components/BatchProcessors/ProcessRecurringTask"));

const InvoiceList = lazy(() => import("../components/Invoicing/InvoiceList"));

// Reports (default exports)
const PayRollReport = lazy(() => import("../components/Reports/PayRollReport"));
const RevenueByServiceType = lazy(() => import("../components/Reports/RevenueByServiceType"));
const RevenueByClient = lazy(() => import("../components/Reports/RevenueByClient"));
const VisitsByClient = lazy(() => import("../components/Reports/VisitsByClient"));

const Unauthorized = lazy(() => import("../pages/error/Unauthorized"));


// Reports (named exports)
const MillageCalculationReport = lazyNamed(
  () => import("../components/Reports/MillageCalculationReport"),
  "MillageCalculationReport"
);

const MedicationHistoryReport = lazyNamed(
  () => import("../components/Reports/MedicationHistoryReport"),
  "MedicationHistoryReport"
);

// NOTE: your file has a trailing space in the filename import path.
// Keep it EXACTLY as this if that’s how it exists on disk.
const CustomerIncidentReport = lazyNamed(
  () => import("../components/Reports/CustomerIncidentReport"),
  "CustomerIncidentReport"
);

// Template/demo routes (keep if you use them)
const AppCalendar = lazy(() => import("../pages/AppCalendar"));
const KanbanBasic = lazy(() => import("../pages/kanban/KanbanBasic"));
const KanbanCustom = lazy(() => import("../pages/kanban/KanbanCustom"));
const Chats = lazy(() => import("../pages/apps/chat/Chats"));
const Inbox = lazy(() => import("../pages/apps/mailbox/Inbox"));

const UserList = lazy(() => import("../pages/user-manage/UserList"));
const UserCards = lazy(() => import("../pages/user-manage/UserCards"));
const UserProfile = lazy(() => import("../pages/user-manage/UserProfile"));
const UserEdit = lazy(() => import("../pages/user-manage/UserEdit"));

const Profile = lazy(() => import("../pages/admin/Profile"));
const ProfileSettings = lazy(() => import("../pages/admin/ProfileSettings"));

const Products = lazy(() => import("../pages/ecommerce/Products"));
const Categories = lazy(() => import("../pages/ecommerce/Categories"));
const AddProduct = lazy(() => import("../pages/ecommerce/AddProduct"));
const EditProduct = lazy(() => import("../pages/ecommerce/EditProduct"));
const AddCategory = lazy(() => import("../pages/ecommerce/AddCategory"));
const EditCategory = lazy(() => import("../pages/ecommerce/EditCategory"));

const Accordion = lazy(() => import("../pages/components/Accordion"));
const Alert = lazy(() => import("../pages/components/Alert"));
const Badge = lazy(() => import("../pages/components/Badge"));
const Breadcrumb = lazy(() => import("../pages/components/Breadcrumb"));
const Buttons = lazy(() => import("../pages/components/Buttons"));
const ButtonGroup = lazy(() => import("../pages/components/ButtonGroup"));
const Cards = lazy(() => import("../pages/components/Cards"));
const Carousel = lazy(() => import("../pages/components/Carousel"));
const CloseButton = lazy(() => import("../pages/components/CloseButton"));
const Collapse = lazy(() => import("../pages/components/Collapse"));
const Dropdowns = lazy(() => import("../pages/components/Dropdowns"));
const ListGroup = lazy(() => import("../pages/components/ListGroup"));
const ModalDemo = lazy(() => import("../pages/components/Modal"));
const Tabs = lazy(() => import("../pages/components/Tabs"));
const Offcanvas = lazy(() => import("../pages/components/Offcanvas"));
const Pagination = lazy(() => import("../pages/components/Pagination"));
const Placeholders = lazy(() => import("../pages/components/Placeholders"));
const Popovers = lazy(() => import("../pages/components/Popovers"));
const Progress = lazy(() => import("../pages/components/Progress"));
const Spinners = lazy(() => import("../pages/components/Spinners"));
const Toasts = lazy(() => import("../pages/components/Toasts"));
const Tooltips = lazy(() => import("../pages/components/Tooltips"));

const Misc = lazy(() => import("../pages/utilities/Misc"));
const Typography = lazy(() => import("../pages/utilities/Typography"));
const Images = lazy(() => import("../pages/utilities/Images"));
const Tables = lazy(() => import("../pages/utilities/Tables"));
const Background = lazy(() => import("../pages/utilities/Background"));
const Borders = lazy(() => import("../pages/utilities/Borders"));
const Colors = lazy(() => import("../pages/utilities/Colors"));
const Flex = lazy(() => import("../pages/utilities/Flex"));
const Sizing = lazy(() => import("../pages/utilities/Sizing"));
const Spacing = lazy(() => import("../pages/utilities/Spacing"));

const Breakpoints = lazy(() => import("../pages/layout/Breakpoints"));
const Containers = lazy(() => import("../pages/layout/Containers"));
const Gutters = lazy(() => import("../pages/layout/Gutters"));

const FormControl = lazy(() => import("../pages/forms/FormControl"));
const FormSelect = lazy(() => import("../pages/forms/FormSelect"));
const DateTime = lazy(() => import("../pages/forms/DateTime"));
const FormUpload = lazy(() => import("../pages/forms/FormUpload"));
const InputGroup = lazy(() => import("../pages/forms/InputGroup"));
const FloatingLabels = lazy(() => import("../pages/forms/FloatingLabels"));
const ChecksRadios = lazy(() => import("../pages/forms/ChecksRadios"));
const FormRange = lazy(() => import("../pages/forms/FormRange"));
const FormValidation = lazy(() => import("../pages/forms/FormValidation"));
const FormLayout = lazy(() => import("../pages/forms/FormLayout"));
const QuillPreview = lazy(() => import("../pages/forms/editors/QuillPreview"));
const TinymcePreview = lazy(() => import("../pages/forms/editors/TinymcePreview"));

const DataTablePreview = lazy(() => import("../pages/DataTablePreview"));
const ChartsPreview = lazy(() => import("../pages/ChartsPreview"));
const Sweetalert = lazy(() => import("../pages/Sweetalert"));
const IconsPreview = lazy(() => import("../pages/IconsPreview"));

const NotFound = lazy(() => import("../pages/error/NotFound"));

const JobScheduleEnhanced = lazy(() => import("../pages/JobScheduleEnhanced"));
const FamilyPortal = lazy(() => import("../pages/FamilyPortal"));

function Router() {
  const { user, loadingApp } = useContext(AppContext);

  const isAdmin = user?.UserRole === "1";
  const isFamily =
    String(user?.UserType || "").toLowerCase() === "family_member" ||
    String(user?.UserRole || "").toLowerCase() === "family";
  const isHarinda = user?.UserName === "Harinda Samaratunga";

  // Prevent flicker while rehydrating context
  if (loadingApp) return null;

  return (
    <ScrollToTop>
      {/* One Suspense boundary for the whole routing tree */}
      <Suspense fallback={<RouteLoading />}>
        <Routes>
          {/* ---------------- Public routes ---------------- */}
          <Route path="auths">
            <Route path="auth-register" element={<AuthRegister />} />
            <Route path="auth-login" element={<AuthLogin />} />
            <Route path="family-login" element={<FamilyAuthLogin />} />
            <Route path="auth-reset" element={<AuthReset />} />
            <Route path="auth-logoff" element={<AuthLogoff />} />
          </Route>

          {/* ---------------- Protected routes ---------------- */}
          <Route
            element={
              <RequireAuth>
                <Outlet />
              </RequireAuth>
            }
          >
            <Route path="blank" element={<Blank />} />

            {/* Role-aware root */}
            {/* {isAdmin && <Route path="/" element={<Home />} />} */}
            {isAdmin && <Route path="/" element={<DashboardModern />} />}
            {isFamily && <Route path="/" element={<FamilyPortal />} />}
            {!isAdmin && !isFamily && <Route path="/" element={<JobScheduleStaffMyAccount />} />}

            <Route
              path="family-portal"
              element={
                <RequireRole allow={["family", "family_member"]}>
                  <FamilyPortal />
                </RequireRole>
              }
            />

            <Route
              element={
                <RequireRole allow={["admin", "user", "1", "0"]}>
                  <Outlet />
                </RequireRole>
              }
            >
              <Route path="job-sheets" element={<Jobsheets />} />
              <Route path="my-job-sheets" element={<Jobsheets restrictedMode={true} />} />
              <Route path="today-visits" element={<TodaysVisitsV2 />} />
              <Route path="today-visits-plus" element={<LiveVisitsBoardRedesigned />} />

              <Route path="clients" element={<Clients />} />
              <Route path="jobsheet/:id" element={<Jobsheet />} />
              <Route path="client/:id" element={<Client />} />

              <Route path="staff/:id" element={<Staff />} />
              <Route path="my-account" element={<MyAccountPage />} />
              <Route path="my-subscription" element={<Subscription />} />

              {/* <Route path="job-schedule" element={<JobSchedule />} /> */}            
              <Route path="job-schedule" element={<JobScheduleEnhanced />} />


              <Route path="job-schedule-manage">
                <Route path="clients-schedule" element={<JobSchedule />} />
                <Route path="staff-schedule" element={<JobScheduleStaff />} />
                <Route path="my-schedule" element={<JobScheduleStaffMyAccount />} />
                <Route path="all-client-schedule" element={<ClientDailyScheduleBoard />} />
              </Route>

              <Route path="reporting">
                <Route path="visits" element={<VisitsByClient />} />
                <Route path="revenuebyclient" element={<RevenueByClient />} />
                <Route path="revenuebyservice" element={<RevenueByServiceType />} />
                <Route path="payrollreport" element={<PayRollReport />} />
                <Route path="millagereport" element={<MillageCalculationReport />} />
                <Route path="medication-history" element={<MedicationHistoryReport />} />
                <Route path="incident-history" element={<CustomerIncidentReport />} />
              </Route>
            </Route>

            {/* ---------------- Admin-only groups ---------------- */}
            <Route
              element={
                <RequireRole allow={["admin", "1"]}>
                  <Outlet />
                </RequireRole>
              }
            >
              <Route path="client-manage">
                <Route path="clients" element={<Clients />} />
                <Route path="job-schedule" element={<JobSchedule />} />
              </Route>

              <Route path="staff-manage">
                <Route path="staff" element={<StaffList />} />
                <Route path="admin-staff" element={<StaffListAdmin />} />
                <Route path="job-schedule" element={<JobScheduleStaff />} />
              </Route>

              <Route path="invoicing">
                <Route path="invoices" element={<InvoiceList />} />
              </Route>

              <Route path="settings">
                <Route path="services" element={<ProductSettings />} />
                <Route path="jobroles" element={<JobRoleSettings />} />
                <Route path="regions" element={<RegionSettings />} />
                <Route path="qualifications" element={<SkillsSettings />} />
              </Route>

              <Route path="batchjobs">
                <Route path="processrecurringtasks" element={<ProcessRecurringTask />} />
              </Route>
            </Route>

            {/* Harinda-only management */}
            {isHarinda && (
              <Route path="management">
                <Route path="tenants" element={<Tenants />} />
                <Route path="apiusage" element={<ApiUsage />} />
                <Route path="subscriptions" element={<Subscriptions />} />
              </Route>
            )}

            {/* Template/demo routes */}
            <Route path="apps">
              <Route path="calendar" element={<AppCalendar />} />
              <Route path="kanban/basic" element={<KanbanBasic />} />
              <Route path="kanban/custom" element={<KanbanCustom />} />
              <Route path="chats" element={<Chats />} />
              <Route path="inbox" element={<Inbox />} />
            </Route>

            <Route path="user-manage">
              <Route path="user-list" element={<UserList />} />
              <Route path="user-cards" element={<UserCards />} />
              <Route path="user-profile/:id" element={<UserProfile />} />
              <Route path="user-edit/:id" element={<UserEdit />} />
            </Route>

            <Route path="admin">
              <Route path="profile" element={<Profile />} />
              <Route path="profile-settings" element={<ProfileSettings />} />
            </Route>

            <Route path="ecommerce">
              <Route path="products" element={<Products />} />
              <Route path="categories" element={<Categories />} />
              <Route path="add-product" element={<AddProduct />} />
              <Route path="edit-product/:id" element={<EditProduct />} />
              <Route path="add-category" element={<AddCategory />} />
              <Route path="edit-category/:id" element={<EditCategory />} />
            </Route>

            <Route path="ui-elements">
              <Route path="accordion" element={<Accordion />} />
              <Route path="alert" element={<Alert />} />
              <Route path="badge" element={<Badge />} />
              <Route path="breadcrumb" element={<Breadcrumb />} />
              <Route path="buttons" element={<Buttons />} />
              <Route path="button-group" element={<ButtonGroup />} />
              <Route path="cards" element={<Cards />} />
              <Route path="carousel" element={<Carousel />} />
              <Route path="close-button" element={<CloseButton />} />
              <Route path="collapse" element={<Collapse />} />
              <Route path="dropdowns" element={<Dropdowns />} />
              <Route path="list-group" element={<ListGroup />} />
              <Route path="modal" element={<ModalDemo />} />
              <Route path="tabs" element={<Tabs />} />
              <Route path="offcanvas" element={<Offcanvas />} />
              <Route path="pagination" element={<Pagination />} />
              <Route path="placeholders" element={<Placeholders />} />
              <Route path="popovers" element={<Popovers />} />
              <Route path="progress" element={<Progress />} />
              <Route path="spinners" element={<Spinners />} />
              <Route path="toasts" element={<Toasts />} />
              <Route path="tooltips" element={<Tooltips />} />
            </Route>

            <Route path="utilities">
              <Route path="misc" element={<Misc />} />
              <Route path="typography" element={<Typography />} />
              <Route path="images" element={<Images />} />
              <Route path="tables" element={<Tables />} />
              <Route path="background" element={<Background />} />
              <Route path="borders" element={<Borders />} />
              <Route path="colors" element={<Colors />} />
              <Route path="flex" element={<Flex />} />
              <Route path="sizing" element={<Sizing />} />
              <Route path="spacing" element={<Spacing />} />
            </Route>

            <Route path="layout">
              <Route path="breakpoints" element={<Breakpoints />} />
              <Route path="containers" element={<Containers />} />
              <Route path="gutters" element={<Gutters />} />
            </Route>

            <Route path="forms">
              <Route path="form-control" element={<FormControl />} />
              <Route path="form-select" element={<FormSelect />} />
              <Route path="date-time" element={<DateTime />} />
              <Route path="form-upload" element={<FormUpload />} />
              <Route path="input-group" element={<InputGroup />} />
              <Route path="floating-labels" element={<FloatingLabels />} />
              <Route path="checks-radios" element={<ChecksRadios />} />
              <Route path="form-range" element={<FormRange />} />
              <Route path="form-validation" element={<FormValidation />} />
              <Route path="form-layout" element={<FormLayout />} />
            </Route>

            <Route path="editors">
              <Route path="quill" element={<QuillPreview />} />
              <Route path="tinymce" element={<TinymcePreview />} />
            </Route>

            <Route path="data-table" element={<DataTablePreview />} />
            <Route path="charts" element={<ChartsPreview />} />
            <Route path="sweetalert" element={<Sweetalert />} />
            <Route path="icons" element={<IconsPreview />} />

            <Route path="not-found" element={<NotFound />} />
            <Route path="*" element={<NotFound />} />
          </Route>

          
<Route
            path="/incidents"
            element={
              <RequireAuth>
                <IncidentsList />
              </RequireAuth>
            }
          />
          <Route
            path="/incidents/:incidentId"
            element={
              <RequireAuth>
                <IncidentDetail />
              </RequireAuth>
            }
          />
          {/* <Route
            path="/incidents/:incidentId/edit"
            element={
              <RequireAuth>
                <IncidentEdit />
              </RequireAuth>
            }
          /> */}

          <Route
            path="/care-plans/:planId"
            element={
              <RequireAuth>
                <CarePlanView />
              </RequireAuth>
            }
          />

          <Route
            path="/care-plans/:planId/print"
            element={
              <RequireAuth>
                <CarePlanPrintView />
              </RequireAuth>
            }
          />
          <Route
            path="/staff-absences"
            element={
              <RequireAuth>
                <StaffAbsencesMonth/>
              </RequireAuth>
            }
          />

         
<Route
  path="/staff-absences/calendar"
  element={
     <RequireAuth>
      <StaffAbsencesCalendarPage />
   </RequireAuth>
  }
/>


          <Route path="unauthorized" element={<Unauthorized />} />



          {/* If somehow a route escapes the protected outlet */}
          <Route path="*" element={<NotFound />} />
          
        </Routes>
      </Suspense>
    </ScrollToTop>
  );
}

export default Router;
